import React, {useCallback, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {PROPERTY_GROUPS} from 'components/Filters/constants'
import {getIsFilterSelected} from 'components/Filters/utils'
import {ShowMoreButton} from 'components/ShowMoreButton'
import {useFiltersForm} from 'hooks/useFiltersForm'
import {useIsSeoSRP} from 'hooks/useIsSeoSRP'
import {
  getGroupedPropertyTypesFilters,
  getIsSearchCompleted
} from 'modules/sapiSearch/selectors'
import {FilterOptionItem, FilterUrlParam} from 'modules/search/types'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {SelectionCard} from '@vio/atlas/SelectionCard'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {cssTheme} from '@vio/atlas/themes'

import {PropertyTypesList} from './PropertyTypesList'

const SelectionCardWrapper = styled.div`
  margin-bottom: ${cssTheme.layout.spacing.s500};
`

const Wrapper = styled.div`
  width: 100%;
  &.--is-popover {
    width: 316px;
    max-height: 268px;
    overflow-y: auto;
    overflow-x: hidden;
    margin-bottom: ${cssTheme.layout.spacing.s500};
    padding: 1px;
    ${SelectionCardWrapper} {
      margin-bottom: ${cssTheme.layout.spacing.s400};
    }
  }
`

const FiltersWrapper = styled.div`
  margin-bottom: ${cssTheme.layout.spacing.s400};
`

const FilterGroupItemWrapper = styled.div`
  &.--has-padding {
    padding-bottom: ${cssTheme.layout.spacing.s500};
  }
`

export interface PropertyTypesProps
  extends Pick<
    ReturnType<typeof useFiltersForm>,
    'setMultiFilters' | 'removeMultiFilters' | 'toggleMultiFilter'
  > {
  isPopover?: boolean
  filters: FilterUrlParam
  appliedFilters: FilterUrlParam
}
const PropertyTypesComponent: React.FC<PropertyTypesProps> = ({
  isPopover,
  filters,
  appliedFilters,
  setMultiFilters,
  removeMultiFilters,
  toggleMultiFilter
}) => {
  const {formatMessage} = useIntl()
  const isSeoSRP = useIsSeoSRP()
  const isSearchCompleted = useSelector(getIsSearchCompleted)
  const propertyTypesFilters = useSelector(getGroupedPropertyTypesFilters)

  const [isLimited, setIsLimited] = useState(true)

  const getIsSelectedGroup = useCallback(
    (facets: FilterOptionItem[]) => {
      return facets.some(facet => getIsFilterSelected(filters, facet.id))
    },
    [filters]
  )

  const getIsDisabledGroup = useCallback(
    (facets: FilterOptionItem[]): boolean => {
      return facets.every(
        facet =>
          !facet.isAvailable && !getIsFilterSelected(appliedFilters, facet.id)
      )
    },
    [appliedFilters]
  )

  const toggleGroup = useCallback(
    (facets: FilterOptionItem[]) => {
      const isSelectedGroup = getIsSelectedGroup(facets)
      if (isSelectedGroup) {
        removeMultiFilters(
          'propertyTypes',
          facets.map(facet => facet.id)
        )

        return
      }
      const availableFacets: string[] = []
      facets.forEach(facet => {
        if (facet.isAvailable) {
          availableFacets.push(facet.id)
        }
      })

      setMultiFilters('propertyTypes', availableFacets)
    },
    [setMultiFilters, getIsSelectedGroup, removeMultiFilters]
  )

  // Showing SkeletonLoader
  if (!isSeoSRP && !isSearchCompleted)
    return (
      <Wrapper className={cx(isPopover && '--is-popover')}>
        <Box display="flex" flexDirection="column" gap="s600">
          <SkeletonLoader />
          <SkeletonLoader marginTop={0} />
        </Box>
      </Wrapper>
    )

  return (
    <Wrapper className={cx(isPopover && '--is-popover')}>
      <FiltersWrapper>
        {PROPERTY_GROUPS.map(group => (
          <FilterGroupItemWrapper
            key={group.type}
            className={cx(!isLimited && '--has-padding')}
          >
            <div>
              <SelectionCardWrapper>
                <SelectionCard
                  name={group.type}
                  selected={getIsSelectedGroup(
                    propertyTypesFilters[group.type]
                  )}
                  disabled={getIsDisabledGroup(
                    propertyTypesFilters[group.type]
                  )}
                  onChange={() => toggleGroup(propertyTypesFilters[group.type])} // We need to use this selection card as a checkbox not a radio button
                  variant="checkbox"
                  prefix={<Icon name={group.header?.groupIcon} size="md" />}
                  title={formatMessage(group.header?.title)}
                >
                  {formatMessage(group.header?.description)}
                </SelectionCard>
              </SelectionCardWrapper>
              {!isLimited && (
                <PropertyTypesList
                  propertyTypes={propertyTypesFilters[group.type]}
                  filters={filters}
                  appliedFilters={appliedFilters}
                  toggleMultiFilter={toggleMultiFilter}
                  isPopover={isPopover}
                />
              )}
            </div>
          </FilterGroupItemWrapper>
        ))}
      </FiltersWrapper>
      <ShowMoreButton
        isLimited={isLimited}
        onToggle={() => setIsLimited(state => !state)}
      />
    </Wrapper>
  )
}

export const PropertyTypes = React.memo(PropertyTypesComponent)
